import React, { useState, useEffect, useRef } from 'react';
import { Send, RefreshCw, Pencil, Trash2, AlertTriangle, Search, Upload, Download, Merge, LayoutGrid, List, Mail, Phone, MessageCircle } from 'lucide-react';
import api from '../../api/axios';
import DataGrid from '../../components/DataGrid';

/* ── Types ───────────────────────────────────────────────────────────────── */

type LeadStatus =
  | 'LEAD_CAPTURED'
  | 'DISCOVERY_CALL_BOOKED'
  | 'CALL_COMPLETED'
  | 'PROPOSAL_SENT'
  | 'PAYMENT_LINK_SENT'
  | 'ENROLLED'
  | 'NOT_INTERESTED';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  source: string;
  status: LeadStatus;
  notes?: string;
  thankYouSentAt?: string;
  createdAt: string;
  updatedAt?: string;
  campaignName?: string;
  product?: string;
}

interface MetaCampaignOption {
  campaignName: string;
  product: string;
}

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

interface MergeResult {
  groupsMerged: number;
  leadsRemoved: number;
}

interface Paged<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

type Envelope<T> = { success?: boolean; data?: T } | T;
function unwrap<T>(raw: Envelope<T>): T {
  return (raw as { data?: T })?.data ?? (raw as T);
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const extractErrorMessage = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
  return data?.message || data?.detail || fallback;
};

const fmt = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_OPTS: LeadStatus[] = [
  'LEAD_CAPTURED', 'DISCOVERY_CALL_BOOKED', 'CALL_COMPLETED',
  'PROPOSAL_SENT', 'PAYMENT_LINK_SENT', 'ENROLLED', 'NOT_INTERESTED',
];

const STATUS_STYLE: Record<LeadStatus, { color: string; bg: string; label: string }> = {
  LEAD_CAPTURED:          { color: '#2563EB', bg: 'rgba(37,99,235,0.1)',   label: 'Lead Captured' },
  DISCOVERY_CALL_BOOKED:  { color: '#D97706', bg: 'rgba(245,158,11,0.1)',  label: 'Discovery Call Booked' },
  CALL_COMPLETED:         { color: '#0EA5E9', bg: 'rgba(14,165,233,0.1)',  label: 'Call Completed' },
  PROPOSAL_SENT:          { color: '#7C3AED', bg: 'rgba(109,40,217,0.1)',  label: 'Proposal Sent' },
  PAYMENT_LINK_SENT:      { color: '#DB2777', bg: 'rgba(219,39,119,0.1)',  label: 'Payment Link Sent' },
  ENROLLED:               { color: '#059669', bg: 'rgba(16,185,129,0.1)',  label: 'Enrolled' },
  NOT_INTERESTED:         { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', label: 'Not Interested' },
};

// a***7@g****.com — keep first char of local part + last char before @, mask the rest.
const maskEmail = (email?: string) => {
  if (!email) return '—';
  const [local, domain] = email.split('@');
  if (!domain || local.length < 2) return email;
  const maskedLocal = `${local[0]}${'*'.repeat(Math.max(local.length - 2, 3))}${local[local.length - 1]}`;
  const domainParts = domain.split('.');
  const maskedDomain = `${domainParts[0][0]}${'*'.repeat(Math.max(domainParts[0].length - 1, 3))}`;
  return `${maskedLocal}@${maskedDomain}.${domainParts.slice(1).join('.')}`;
};

// +91********86 — keep leading digits + last 2, mask the middle.
const maskPhone = (phone?: string) => {
  if (!phone) return undefined;
  const hasPlus = phone.trim().startsWith('+');
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 4) return phone;
  const visibleStart = digits.slice(0, 2);
  const visibleEnd = digits.slice(-2);
  const masked = '*'.repeat(Math.max(digits.length - 4, 4));
  return `${hasPlus ? '+' : ''}${visibleStart}${masked}${visibleEnd}`;
};

const waLink = (phone: string) => `https://wa.me/${phone.replace(/\D/g, '')}`;

/* ── LeadManagementPage ──────────────────────────────────────────────────── */

const PAGE_SIZE = 10;

const LeadManagementPage: React.FC = () => {
  /* add-lead form */
  const [name,  setName]  = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [campaign, setCampaign] = useState('');
  const [product, setProduct] = useState('NEXORA');
  const [campaignOptions, setCampaignOptions] = useState<MetaCampaignOption[]>([]);
  const [adding,  setAdding]  = useState(false);
  const [addError, setAddError] = useState('');

  /* leads list */
  const [leads,         setLeads]         = useState<Lead[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [page,          setPage]          = useState(0);
  const [totalPages,    setTotalPages]    = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter,  setStatusFilter]  = useState<LeadStatus | ''>('');
  const [search,        setSearch]        = useState('');
  const [searchInput,   setSearchInput]   = useState('');

  /* selection + sending */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [sendError, setSendError] = useState('');

  /* notes editing */
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');

  /* edit lead modal */
  const [editLead,   setEditLead]   = useState<Lead | null>(null);
  const [editName,   setEditName]   = useState('');
  const [editEmail,  setEditEmail]  = useState('');
  const [editPhone,  setEditPhone]  = useState('');
  const [editCampaign, setEditCampaign] = useState('');
  const [editProduct,  setEditProduct]  = useState('NEXORA');
  const [editSaving, setEditSaving] = useState(false);
  const [editError,  setEditError]  = useState('');

  /* delete confirm */
  const [deleteId,    setDeleteId]    = useState<string | null>(null);
  const [deleting,    setDeleting]    = useState(false);
  const [deleteError, setDeleteError] = useState('');

  /* bulk delete confirm */
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting,   setBulkDeleting]   = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState('');

  /* csv import */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError,  setImportError]  = useState('');
  const [exporting,    setExporting]    = useState(false);
  const [syncingMeta,  setSyncingMeta]  = useState(false);

  /* merge duplicates */
  const [merging,     setMerging]     = useState(false);
  const [mergeResult, setMergeResult] = useState<MergeResult | null>(null);
  const [mergeError,  setMergeError]  = useState('');

  /* board view */
  const [view, setView] = useState<'table' | 'board'>('table');
  const [boardLeads, setBoardLeads] = useState<Lead[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [dragOverStatus, setDragOverStatus] = useState<LeadStatus | null>(null);

  /* ── Data fetchers ── */

  const fetchLeads = async (p: number, status: LeadStatus | '' = statusFilter, q: string = search) => {
    setLoading(true);
    try {
      const res   = await api.get('/api/v1/admin/leads', {
        params: { page: p, size: PAGE_SIZE, status: status || undefined, search: q || undefined },
      });
      const paged = unwrap<Paged<Lead>>(res.data);
      setLeads(paged.content ?? []);
      setTotalPages(paged.totalPages ?? 0);
      setTotalElements(paged.totalElements ?? 0);
    } catch { /* empty state shown */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchLeads(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/api/v1/admin/meta/campaigns')
      .then(res => setCampaignOptions(unwrap<MetaCampaignOption[]>(res.data) ?? []))
      .catch(() => { /* dropdown just falls back to empty */ });
  }, []);

  // Debounce free-text search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(0);
        fetchLeads(0, statusFilter, searchInput);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const fetchBoardLeads = async () => {
    setBoardLoading(true);
    try {
      const res = await api.get('/api/v1/admin/leads', { params: { page: 0, size: 500 } });
      const paged = unwrap<Paged<Lead>>(res.data);
      setBoardLeads(paged.content ?? []);
    } catch { /* empty state shown */ } finally { setBoardLoading(false); }
  };

  useEffect(() => {
    if (view === 'board') fetchBoardLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleBoardDrop = async (leadId: string, status: LeadStatus) => {
    const lead = boardLeads.find(l => l.id === leadId);
    if (!lead || lead.status === status) return;
    const snapshot = boardLeads;
    setBoardLeads(ls => ls.map(l => l.id === leadId ? { ...l, status } : l));
    setLeads(ls => ls.map(l => l.id === leadId ? { ...l, status } : l));
    try {
      const res = await api.patch(`/api/v1/admin/leads/${leadId}/status`, { status, notes: lead.notes });
      const updated = unwrap<Lead>(res.data);
      setBoardLeads(ls => ls.map(l => l.id === leadId ? updated : l));
      setLeads(ls => ls.map(l => l.id === leadId ? updated : l));
    } catch {
      setBoardLeads(snapshot);
    }
  };

  const goToPage = (p: number) => { setPage(p); fetchLeads(p); };

  const changeStatusFilter = (status: LeadStatus | '') => {
    setStatusFilter(status);
    setPage(0);
    fetchLeads(0, status);
  };

  /* ── Handlers ── */

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(''); setAdding(true);
    try {
      await api.post('/api/v1/admin/leads', {
        name, email, phone: phone || undefined,
        product, campaignLabel: campaign || undefined,
      });
      setName(''); setEmail(''); setPhone(''); setCampaign(''); setProduct('NEXORA');
      fetchLeads(page);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAddError(msg || 'Failed to add lead. Please try again.');
    } finally { setAdding(false); }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true); setImportError(''); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/v1/admin/leads/import', formData, {
        headers: { 'Content-Type': undefined },
      });
      setImportResult(unwrap<ImportResult>(res.data));
      fetchLeads(0);
      setPage(0);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setImportError(msg || 'Failed to import CSV. Please check the file and try again.');
    } finally { setImporting(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/api/v1/admin/leads/export', {
        params: { status: statusFilter || undefined, search: search || undefined },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch { /* leave state as-is; user can retry */ }
    finally { setExporting(false); }
  };

  const handleSyncMeta = async () => {
    setSyncingMeta(true);
    try {
      await api.post('/api/v1/admin/meta/campaigns/sync-now');
      await fetchLeads(0);
    } catch { /* transient Meta API errors are logged server-side; just leave state as-is */ }
    finally { setSyncingMeta(false); }
  };

  const handleMergeDuplicates = async () => {
    setMerging(true); setMergeError(''); setMergeResult(null);
    try {
      const res = await api.post('/api/v1/admin/leads/merge-duplicates');
      setMergeResult(unwrap<MergeResult>(res.data));
      fetchLeads(0);
      setPage(0);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMergeError(msg || 'Failed to merge duplicates. Please try again.');
    } finally { setMerging(false); }
  };

  const toggleSelect = (id: string) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(s => s.size === leads.length ? new Set() : new Set(leads.map(l => l.id)));
  };

  const handleSendOne = async (id: string) => {
    setSendError('');
    setSendingIds(s => new Set(s).add(id));
    try {
      const res = await api.post(`/api/v1/admin/leads/${id}/send-thank-you`);
      const updated = unwrap<Lead>(res.data);
      setLeads(ls => ls.map(l => l.id === id ? updated : l));
    } catch (err: unknown) {
      setSendError(extractErrorMessage(err, 'Failed to send thank-you email. Please try again.'));
    } finally { setSendingIds(s => { const n = new Set(s); n.delete(id); return n; }); }
  };

  const handleSendBulk = async () => {
    if (selected.size === 0) return;
    setSendError('');
    setBulkSending(true);
    try {
      await api.post('/api/v1/admin/leads/send-thank-you', { ids: Array.from(selected) });
      setSelected(new Set());
      fetchLeads(page);
    } catch (err: unknown) {
      setSendError(extractErrorMessage(err, 'Failed to send thank-you emails. Please try again.'));
    } finally { setBulkSending(false); }
  };

  const handleStatusChange = async (lead: Lead, status: LeadStatus) => {
    const snapshot = leads;
    setLeads(ls => ls.map(l => l.id === lead.id ? { ...l, status } : l));
    try {
      const res = await api.patch(`/api/v1/admin/leads/${lead.id}/status`, { status, notes: lead.notes });
      const updated = unwrap<Lead>(res.data);
      setLeads(ls => ls.map(l => l.id === lead.id ? updated : l));
    } catch { setLeads(snapshot); }
  };

  const openNotesEditor = (lead: Lead) => {
    setEditingNotesId(lead.id);
    setNotesDraft(lead.notes ?? '');
  };

  const saveNotes = async (lead: Lead) => {
    const snapshot = leads;
    setEditingNotesId(null);
    setLeads(ls => ls.map(l => l.id === lead.id ? { ...l, notes: notesDraft } : l));
    try {
      const res = await api.patch(`/api/v1/admin/leads/${lead.id}/status`, { status: lead.status, notes: notesDraft });
      const updated = unwrap<Lead>(res.data);
      setLeads(ls => ls.map(l => l.id === lead.id ? updated : l));
    } catch { setLeads(snapshot); }
  };

  const openEdit = (lead: Lead) => {
    setEditError('');
    setEditLead(lead);
    setEditName(lead.name);
    setEditEmail(lead.email);
    setEditPhone(lead.phone ?? '');
    setEditCampaign(lead.campaignName ?? '');
    setEditProduct(lead.product ?? 'NEXORA');
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editLead) return;
    setEditError(''); setEditSaving(true);
    try {
      const res = await api.put(`/api/v1/admin/leads/${editLead.id}`, {
        name: editName, email: editEmail, phone: editPhone || undefined,
        product: editProduct, campaignLabel: editCampaign,
      });
      const updated = unwrap<Lead>(res.data);
      setLeads(ls => ls.map(l => l.id === updated.id ? updated : l));
      setEditLead(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setEditError(msg || 'Failed to save changes. Please try again.');
    } finally { setEditSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const snapshot = [...leads];
    setDeleting(true); setDeleteError('');
    setLeads(ls => ls.filter(l => l.id !== deleteId));
    try {
      await api.delete(`/api/v1/admin/leads/${deleteId}`);
      setDeleteId(null);
      setTotalElements(n => n - 1);
      setSelected(s => { const n = new Set(s); n.delete(deleteId); return n; });
    } catch {
      setLeads(snapshot);
      setDeleteError('Failed to delete lead. Please try again.');
    } finally { setDeleting(false); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const snapshot = [...leads];
    setBulkDeleting(true); setBulkDeleteError('');
    setLeads(ls => ls.filter(l => !selected.has(l.id)));
    try {
      await api.post('/api/v1/admin/leads/delete-bulk', { ids });
      setTotalElements(n => Math.max(0, n - ids.length));
      setSelected(new Set());
      setShowBulkDelete(false);
    } catch {
      setLeads(snapshot);
      setBulkDeleteError('Failed to delete leads. Please try again.');
    } finally { setBulkDeleting(false); }
  };

  /* ── Column definitions ── */

  const columns = [
    {
      key: 'select', header: '',
      headerStyle: { width: 36 },
      render: (l: Lead) => (
        <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)} />
      ),
    },
    {
      key: 'name', header: 'Name',
      cellStyle: { fontWeight: 500 },
      render: (l: Lead) => l.name,
    },
    {
      key: 'email', header: 'Email',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (l: Lead) => l.email,
    },
    {
      key: 'phone', header: 'Phone',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (l: Lead) => l.phone ?? '—',
    },
    {
      key: 'campaign', header: 'Campaign',
      cellStyle: { color: 'var(--nx-text-secondary)', maxWidth: 160, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
      render: (l: Lead) => <span title={l.campaignName ?? undefined}>{l.campaignName ?? '—'}</span>,
    },
    {
      key: 'product', header: 'Product',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (l: Lead) => l.product ?? '—',
    },
    {
      key: 'status', header: 'Status',
      render: (l: Lead) => (
        <select
          value={l.status}
          onChange={e => handleStatusChange(l, e.target.value as LeadStatus)}
          style={{
            border: 'none', background: STATUS_STYLE[l.status].bg, color: STATUS_STYLE[l.status].color,
            fontSize: 11, fontWeight: 600, borderRadius: 99, padding: '3px 8px', cursor: 'pointer',
          }}
        >
          {STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_STYLE[s].label}</option>)}
        </select>
      ),
    },
    {
      key: 'thankYou', header: 'Thank-you sent',
      cellStyle: { color: 'var(--nx-text-secondary)', whiteSpace: 'nowrap' as const },
      render: (l: Lead) => fmt(l.thankYouSentAt),
    },
    {
      key: 'notes', header: 'Notes',
      cellStyle: { maxWidth: 220 },
      render: (l: Lead) => editingNotesId === l.id ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            autoFocus
            className="nx-input"
            style={{ fontSize: 12 }}
            value={notesDraft}
            onChange={e => setNotesDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveNotes(l); if (e.key === 'Escape') setEditingNotesId(null); }}
          />
          <button onClick={() => saveNotes(l)} className="nx-btn nx-btn-outline nx-btn-sm">Save</button>
        </div>
      ) : (
        <span
          onClick={() => openNotesEditor(l)}
          title="Click to edit"
          style={{ cursor: 'pointer', color: l.notes ? 'var(--nx-text-primary)' : 'var(--nx-text-muted)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {l.notes || 'Add note…'}
        </span>
      ),
    },
    {
      key: 'actions', header: '',
      cellStyle: { textAlign: 'right' as const },
      render: (l: Lead) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button
            onClick={() => handleSendOne(l.id)}
            disabled={sendingIds.has(l.id)}
            className="nx-btn nx-btn-outline nx-btn-sm"
            style={{ gap: 6, opacity: sendingIds.has(l.id) ? 0.7 : 1 }}
          >
            <Send size={12} />
            {sendingIds.has(l.id) ? 'Sending…' : l.thankYouSentAt ? 'Resend' : 'Send Thank You'}
          </button>
          <button
            onClick={() => openEdit(l)}
            className="nx-btn nx-btn-outline nx-btn-sm"
            title="Edit lead"
          >
            <Pencil size={12} />
          </button>
          <button
            onClick={() => { setDeleteError(''); setDeleteId(l.id); }}
            title="Delete lead"
            style={{
              padding: '5px 8px', borderRadius: 6,
              border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)',
              color: '#DC2626', cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <Trash2 size={12} />
          </button>
        </div>
      ),
    },
  ];

  /* ── Render ── */

  return (
    <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Leads
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            Track leads from Meta ad campaigns and send them a thank-you email.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--nx-surface-2, #f1f5f9)', padding: 3, borderRadius: 9, border: '1px solid var(--nx-border)' }}>
          <button
            onClick={() => setView('table')}
            className="nx-btn nx-btn-sm"
            style={{
              gap: 6, border: 'none',
              background: view === 'table' ? '#fff' : 'transparent',
              boxShadow: view === 'table' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              color: view === 'table' ? 'var(--nx-text-primary)' : 'var(--nx-text-muted)',
            }}
          >
            <List size={13} /> Table
          </button>
          <button
            onClick={() => setView('board')}
            className="nx-btn nx-btn-sm"
            style={{
              gap: 6, border: 'none',
              background: view === 'board' ? '#fff' : 'transparent',
              boxShadow: view === 'board' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              color: view === 'board' ? 'var(--nx-text-primary)' : 'var(--nx-text-muted)',
            }}
          >
            <LayoutGrid size={13} /> Board
          </button>
        </div>
      </div>

      {sendError && (
        <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 12.5, color: 'var(--nx-red)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>{sendError}</span>
          <button
            onClick={() => setSendError('')}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline', flexShrink: 0 }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Add lead form card ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', padding: '22px 24px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
            Add Lead
          </h2>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportFile}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={handleImportClick}
              disabled={importing}
              className="nx-btn nx-btn-outline nx-btn-sm"
              style={{ gap: 6, opacity: importing ? 0.7 : 1, cursor: importing ? 'not-allowed' : 'pointer' }}
            >
              <Upload size={12} />
              {importing ? 'Importing…' : 'Import CSV'}
            </button>
            <button
              type="button"
              onClick={handleMergeDuplicates}
              disabled={merging}
              className="nx-btn nx-btn-outline nx-btn-sm"
              title="Merge leads that share the same email"
              style={{ gap: 6, opacity: merging ? 0.7 : 1, cursor: merging ? 'not-allowed' : 'pointer' }}
            >
              <Merge size={12} />
              {merging ? 'Merging…' : 'Merge Duplicates'}
            </button>
          </div>
        </div>

        {importError && (
          <p style={{ fontSize: 12, color: 'var(--nx-red)', marginBottom: 14 }}>{importError}</p>
        )}
        {importResult && (
          <div style={{ fontSize: 12, color: 'var(--nx-text-secondary)', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
            Import complete — {importResult.created} added, {importResult.updated} updated
            {importResult.skipped > 0 ? `, ${importResult.skipped} skipped` : ''}.
            {importResult.errors.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                {importResult.errors.length > 10 && <li>…and {importResult.errors.length - 10} more</li>}
              </ul>
            )}
          </div>
        )}
        {mergeError && (
          <p style={{ fontSize: 12, color: 'var(--nx-red)', marginBottom: 14 }}>{mergeError}</p>
        )}
        {mergeResult && (
          <div style={{ fontSize: 12, color: 'var(--nx-text-secondary)', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
            {mergeResult.groupsMerged === 0
              ? 'No duplicate leads found.'
              : `Merged ${mergeResult.groupsMerged} duplicate group${mergeResult.groupsMerged === 1 ? '' : 's'} — removed ${mergeResult.leadsRemoved} duplicate lead${mergeResult.leadsRemoved === 1 ? '' : 's'}.`}
          </div>
        )}

        <form onSubmit={handleAdd}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '14px 20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Name
              </label>
              <input required className="nx-input" value={name} onChange={e => setName(e.target.value)} placeholder="Priya Sharma" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Email
              </label>
              <input required type="email" className="nx-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="priya@example.com" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Phone
              </label>
              <input className="nx-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Campaign
              </label>
              <input
                className="nx-input" list="add-lead-campaigns" value={campaign}
                onChange={e => setCampaign(e.target.value)} placeholder="Pick or type a campaign"
              />
              <datalist id="add-lead-campaigns">
                {Array.from(new Set(
                  campaignOptions
                    .filter(c => c.product === product)
                    .map(c => c.campaignName)
                )).map(name => <option key={name} value={name} />)}
              </datalist>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Product
              </label>
              <select className="nx-input" value={product} onChange={e => { setProduct(e.target.value); setCampaign(''); }}>
                <option value="NEXORA">NEXORA</option>
                <option value="KULALI">KULALI</option>
                <option value="PENTAFLOW">PENTAFLOW</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 14, marginTop: 4 }}>
              <button
                type="submit"
                disabled={adding}
                className="nx-btn nx-btn-primary nx-btn-sm"
                style={{ opacity: adding ? 0.7 : 1, cursor: adding ? 'not-allowed' : 'pointer' }}
              >
                {adding ? 'Adding…' : 'Add Lead'}
              </button>
              {addError && <span style={{ fontSize: 12, color: 'var(--nx-red)' }}>{addError}</span>}
            </div>
          </div>
        </form>
      </div>

      {/* ── Leads table card ── */}
      {view === 'table' && (
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
              All Leads
            </span>
            {totalElements > 0 && (
              <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{totalElements} total</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)' }} />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search name, email, phone…"
                className="nx-input"
                style={{ fontSize: 12, padding: '6px 10px 6px 28px', width: 200 }}
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => changeStatusFilter(e.target.value as LeadStatus | '')}
              className="nx-input"
              style={{ fontSize: 12, padding: '6px 10px' }}
            >
              <option value="">All statuses</option>
              {STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_STYLE[s].label}</option>)}
            </select>
            <button
              onClick={handleSendBulk}
              disabled={selected.size === 0 || bulkSending}
              className="nx-btn nx-btn-primary nx-btn-sm"
              style={{ gap: 6, opacity: selected.size === 0 || bulkSending ? 0.6 : 1, cursor: selected.size === 0 || bulkSending ? 'not-allowed' : 'pointer' }}
            >
              <Send size={12} />
              {bulkSending ? 'Sending…' : `Send Thank You (${selected.size})`}
            </button>
            <button
              onClick={() => { setBulkDeleteError(''); setShowBulkDelete(true); }}
              disabled={selected.size === 0}
              className="nx-btn nx-btn-outline nx-btn-sm"
              style={{
                gap: 6, opacity: selected.size === 0 ? 0.6 : 1, cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
                color: '#DC2626', borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)',
              }}
            >
              <Trash2 size={12} />
              {`Delete Selected (${selected.size})`}
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="nx-btn nx-btn-outline nx-btn-sm"
              style={{ gap: 6, opacity: exporting ? 0.7 : 1, cursor: exporting ? 'not-allowed' : 'pointer' }}
            >
              <Download size={12} /> {exporting ? 'Exporting…' : 'Export'}
            </button>
            <button onClick={() => fetchLeads(page)} className="nx-btn nx-btn-outline nx-btn-sm" style={{ gap: 6 }}>
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              onClick={handleSyncMeta}
              disabled={syncingMeta}
              className="nx-btn nx-btn-outline nx-btn-sm"
              style={{ gap: 6, opacity: syncingMeta ? 0.7 : 1, cursor: syncingMeta ? 'not-allowed' : 'pointer' }}
              title="Pull in the latest leads from Meta right now instead of waiting for the automatic sync"
            >
              <RefreshCw size={12} style={syncingMeta ? { animation: 'nx-spin 1s linear infinite' } : undefined} />
              {syncingMeta ? 'Syncing from Meta…' : 'Sync from Meta'}
            </button>
          </div>
        </div>

        {leads.length > 0 && (
          <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={selected.size === leads.length} onChange={toggleSelectAll} />
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Select all on page</span>
          </div>
        )}

        <DataGrid
          columns={columns}
          rows={leads}
          rowKey={l => l.id}
          loading={loading}
          emptyText={search || statusFilter ? 'No leads match your filters.' : 'No leads yet — add one above.'}
          page={page}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      </div>
      )}

      {/* ── Leads board (Kanban) ── */}
      {view === 'board' && (
        <div>
          {boardLoading ? (
            <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
              <span style={{ width: 24, height: 24, border: '2px solid var(--nx-border)', borderTopColor: '#1A6CF6', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
              {STATUS_OPTS.map(status => {
                const statusLeads = boardLeads.filter(l => l.status === status);
                const isDragOver = dragOverStatus === status;
                return (
                  <div
                    key={status}
                    onDragOver={e => { e.preventDefault(); setDragOverStatus(status); }}
                    onDragLeave={() => setDragOverStatus(s => (s === status ? null : s))}
                    onDrop={e => {
                      e.preventDefault();
                      setDragOverStatus(null);
                      const leadId = e.dataTransfer.getData('text/plain');
                      if (leadId) handleBoardDrop(leadId, status);
                    }}
                    style={{
                      flex: '0 0 260px', display: 'flex', flexDirection: 'column',
                      background: isDragOver ? 'rgba(26,108,246,0.06)' : '#fff',
                      border: isDragOver ? '1px dashed #1A6CF6' : '1px solid var(--nx-border)',
                      borderRadius: 12, maxHeight: 'calc(100vh - 260px)',
                    }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderBottom: '1px solid var(--nx-border)',
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_STYLE[status].color }}>
                        {STATUS_STYLE[status].label}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: STATUS_STYLE[status].color,
                        background: STATUS_STYLE[status].bg, borderRadius: 20, padding: '1px 8px',
                      }}>
                        {statusLeads.length}
                      </span>
                    </div>
                    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
                      {statusLeads.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', padding: '16px 4px', textAlign: 'center' }}>
                          No leads
                        </div>
                      ) : statusLeads.map(lead => (
                        <div
                          key={lead.id}
                          draggable
                          onDragStart={e => e.dataTransfer.setData('text/plain', lead.id)}
                          style={{
                            background: 'var(--nx-surface-2, #f8fafc)', border: '1px solid var(--nx-border)',
                            borderRadius: 9, padding: '10px 12px', cursor: 'grab',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{
                              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                              background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
                              color: '#fff', fontSize: 11, fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {lead.name?.[0]?.toUpperCase() ?? '?'}
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {lead.name}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 2, overflow: 'hidden' }}>
                            <Mail size={11} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{maskEmail(lead.email)}</span>
                          </div>
                          {lead.phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 6 }}>
                              <Phone size={11} style={{ flexShrink: 0 }} />
                              <span>{maskPhone(lead.phone)}</span>
                            </div>
                          )}
                          {(lead.campaignName || lead.source === 'META_ADS') && (
                            <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {lead.campaignName && (
                                <span style={{
                                  fontSize: 10, color: 'var(--nx-text-secondary)', overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  📣 {lead.campaignName}
                                </span>
                              )}
                              {lead.source === 'META_ADS' && (
                                <span style={{
                                  alignSelf: 'flex-start', fontSize: 9, fontWeight: 700, letterSpacing: '0.03em',
                                  color: '#1A6CF6', background: 'rgba(26,108,246,0.1)', borderRadius: 4, padding: '1px 6px',
                                }}>
                                  META
                                </span>
                              )}
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginBottom: 8 }}>
                            {fmt(lead.createdAt)}{lead.updatedAt && lead.updatedAt !== lead.createdAt ? ` · updated ${fmt(lead.updatedAt)}` : ''}
                          </div>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            {lead.phone && (
                              <a
                                href={waLink(lead.phone)}
                                target="_blank" rel="noreferrer"
                                title="Chat on WhatsApp"
                                style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: '#059669', display: 'flex', textDecoration: 'none' }}
                              >
                                <MessageCircle size={11} />
                              </a>
                            )}
                            <button
                              onClick={() => handleSendOne(lead.id)}
                              disabled={sendingIds.has(lead.id)}
                              title={lead.thankYouSentAt ? 'Resend thank-you' : 'Send thank-you'}
                              style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid var(--nx-border)', background: '#fff', cursor: 'pointer', display: 'flex' }}
                            >
                              <Send size={11} />
                            </button>
                            <button
                              onClick={() => openEdit(lead)}
                              title="Edit lead"
                              style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid var(--nx-border)', background: '#fff', cursor: 'pointer', display: 'flex' }}
                            >
                              <Pencil size={11} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ════════════ EDIT LEAD MODAL ════════════ */}
      {editLead && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setEditLead(null); }}
        >
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 28px', maxWidth: 420, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 18, fontFamily: 'Syne, sans-serif' }}>
              Edit Lead
            </p>
            <form onSubmit={handleEditSave}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Name
                  </label>
                  <input required className="nx-input" value={editName} onChange={e => setEditName(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Email
                  </label>
                  <input required type="email" className="nx-input" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Phone
                  </label>
                  <input className="nx-input" value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Product
                  </label>
                  <select className="nx-input" value={editProduct} onChange={e => setEditProduct(e.target.value)}>
                    <option value="NEXORA">NEXORA</option>
                    <option value="KULALI">KULALI</option>
                    <option value="PENTAFLOW">PENTAFLOW</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Campaign
                  </label>
                  <input
                    className="nx-input" list="edit-lead-campaigns" value={editCampaign}
                    onChange={e => setEditCampaign(e.target.value)} placeholder="Pick or type a campaign"
                  />
                  <datalist id="edit-lead-campaigns">
                    {Array.from(new Set(
                      campaignOptions
                        .filter(c => c.product === editProduct)
                        .map(c => c.campaignName)
                    )).map(name => <option key={name} value={name} />)}
                  </datalist>
                </div>
                {editError && <span style={{ fontSize: 12, color: 'var(--nx-red)' }}>{editError}</span>}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => setEditLead(null)}
                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--nx-border)', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editSaving}
                    className="nx-btn nx-btn-primary nx-btn-sm"
                    style={{ opacity: editSaving ? 0.7 : 1, cursor: editSaving ? 'not-allowed' : 'pointer' }}
                  >
                    {editSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════ DELETE CONFIRM DIALOG ════════════ */}
      {deleteId && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) { setDeleteId(null); setDeleteError(''); } }}
        >
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 28px', maxWidth: 420, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AlertTriangle size={20} style={{ color: '#DC2626' }} />
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 5, fontFamily: 'Syne, sans-serif' }}>
                  Delete lead?
                </p>
                <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
                  This removes the lead from your list. It won't show up in searches or lists anymore, but the record is kept for your history.
                </p>
              </div>
            </div>
            {deleteError && (
              <p style={{ fontSize: 12, color: 'var(--nx-red)', marginBottom: 14 }}>{deleteError}</p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteId(null); setDeleteError(''); }}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--nx-border)', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: '#fff', cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600, opacity: deleting ? 0.7 : 1,
                  fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {deleting && (
                  <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
                )}
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════ BULK DELETE CONFIRM DIALOG ════════════ */}
      {showBulkDelete && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) { setShowBulkDelete(false); setBulkDeleteError(''); } }}
        >
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 28px', maxWidth: 420, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AlertTriangle size={20} style={{ color: '#DC2626' }} />
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 5, fontFamily: 'Syne, sans-serif' }}>
                  Delete {selected.size} lead{selected.size === 1 ? '' : 's'}?
                </p>
                <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
                  This removes the selected leads from your list. They won't show up in searches or lists anymore, but the records are kept for your history.
                </p>
              </div>
            </div>
            {bulkDeleteError && (
              <p style={{ fontSize: 12, color: 'var(--nx-red)', marginBottom: 14 }}>{bulkDeleteError}</p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowBulkDelete(false); setBulkDeleteError(''); }}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--nx-border)', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: '#fff', cursor: bulkDeleting ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600, opacity: bulkDeleting ? 0.7 : 1,
                  fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {bulkDeleting && (
                  <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
                )}
                {bulkDeleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagementPage;
